// @ts-nocheck
import React, { useContext, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { CheckboxChangeParams } from "primereact/checkbox";
import { Button, Checkbox, Dialog } from "iuvity-ui-components";
import { Upload } from "iuvity-ui-components/dist/upload";
import { CustomInput } from "../inputs/CustomInput";
import { DialogProps } from "../dialog/CustomDialog";
import { MultiContentDialogProps } from "../dialog/MultiContentDialog";
import { ConfigsExportOptions, REVALIDATE_FORM_PROPS } from "../../constants";
import { Environment } from "../../interfaces";
import { AppContext } from "../../context";

interface EnvImporterProps {
  env: Environment;
  importFn: Function;
  modalController: React.Dispatch<
    React.SetStateAction<MultiContentDialogProps>
  >;
  auxModalController: React.Dispatch<React.SetStateAction<DialogProps>>;
}

const importerDefaultValues = {
  ...ConfigsExportOptions.reduce((a, v) => ({ ...a, [v.value]: false }), {}),
  all: false,
};

export const EnvImporter: React.FC<EnvImporterProps> = ({
  env,
  importFn,
  modalController,
  auxModalController,
}) => {
  const {
    t,
    appState: { user, selectedApplication },
  } = useContext(AppContext);

  const { control, getValues, reset, setValue, watch } = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: importerDefaultValues,
  });

  const formValues = watch();

  const [filePath, setFilePath] = useState<string>("");
  const [uploadedData, setUploadedData] = useState({});
  const [filteredForm, setFilteredForm] = useState<any[]>([]);
  const [uploadErrorMsg, setuploadErrorMsg] = useState<boolean>(false);

  const onCancel = () => {
    modalController((prev) => ({
      ...prev,
      visible: false,
      children: null,
    }));
  };

  const onImportApp = async () => {
    const { all, ...rest } = getValues();
    const keysToimport = Object.entries(rest)
      .filter(([_, value]) => value)
      .map(([key, _]) => key);
    const itemsToImport = keysToimport.reduce((obj, key) => {
      const isProductInfo = ["products", "productsinfo"].includes(key);
      if (isProductInfo) {
        const secKey: string =
          key === "products" ? "configurations" : "information";
        return {
          ...obj,
          products: {
            ...(obj as any)["products"],
            [secKey]: (uploadedData as any)["products"][secKey],
          },
        };
      } else {
        return { ...obj, [key]: (uploadedData as any)[key] };
      }
    }, {});
    onCancel();
    await importFn("IMPORT", {
      appData: {
        appid: env?.applicationid,
        envid: env?.id,
        updatedby: user?.name,
      },
      itemsToImport,
    });
  };

  const handleModalProps = () => {
    auxModalController((prev) => ({
      ...prev,
      visible: true,
      title: t(`Applications.importModalTitle`),
      content: `${t(`Applications.importModalContent`)} "${
        selectedApplication?.name
      } - ${selectedApplication?.segment}".`,
      onConfirm: onImportApp,
    }));
  };

  const invoiceUploadHandler = ({ files }: any) => {
    const [file] = files;
    const fileReader = new FileReader();
    fileReader.readAsText(file, "UTF-8");
    fileReader.onload = (e) => {
      setFilePath(file.name);
      try {
        const newData = JSON.parse(`${e.target?.result}`);
        setUploadedData(newData);
        setFilteredForm(
          Object.keys(importerDefaultValues).filter((key) => {
            if (key === "products") {
              return newData?.products?.configurations;
            } else if (key === "productsinfo") {
              return newData?.products?.information;
            } else {
              return Object.keys(newData).includes(key);
            }
          })
        );
      } catch (_) {
        setuploadErrorMsg(true);
      }
    };
  };

  const invoiceRemoveHandler = () => {
    setUploadedData({});
    setFilePath("");
    setuploadErrorMsg(false);
    setFilteredForm([]);
    reset(importerDefaultValues);
  };

  const renderFooter = () => {
    return (
      <div className="flex align-items-center justify-content-center">
        <Button
          label={t("Applications.cancelBtnLabel")}
          onClick={onCancel}
          className="mr-4"
          variant="p-button-link"
        />
        <Button
          label={t("Applications.importBtnLabel")}
          variant="p-button"
          onClick={handleModalProps}
          disabled={!Object.values(formValues).some((item) => item)}
        />
      </div>
    );
  };

  return (
    <Dialog
      id="dialogScroll1"
      className="dialog-form"
      header={t("Applications.importApplicationTitle")}
      visible={true}
      onHide={onCancel}
      style={{ width: "50rem" }}
      footer={renderFooter}
    >
      <>
        <div
          className={`upload-container ${
            Object.keys(uploadedData).length > 0 ? "mb-5" : ""
          }`}
        >
          <div className="upload-input-container mr-4">
            <span
              style={{
                textAlign: "center",
              }}
            >
              {t("Applications.importAppName")}
            </span>
            <CustomInput
              id="input-file"
              className="upload-input rd-only"
              placeholder={t(`Applications.uploadFilePlaceholder`)}
              type="text"
              autoComplete="off"
              readOnly
              value={filePath}
            />
          </div>
          <Upload
            chooseLabel={
              Object.keys(uploadedData).length > 0
                ? t("Applications.uploadedBtnLabel")
                : t("Applications.uploadBtnLabel")
            }
            mode="basic"
            accept="application/JSON"
            customUpload={true}
            onClear={invoiceRemoveHandler}
            uploadHandler={invoiceUploadHandler}
            auto={true}
            disabled={Object.keys(uploadedData).length > 0}
          />
        </div>
        {uploadErrorMsg && (
          <span className="p-error block mt-5">
            {t("Applications.uploadFileErrorMsgLabel")}
          </span>
        )}
        {filteredForm.length > 0 && (
          <>
            <span className="mb-5">
              {`${t(
                "Applications.importApplicationDescripion"
              )} "${filePath}".`}
            </span>

            <div className="form sm mt-5 px-5 gap-3">
              {[
                ...(filteredForm.length > 1 ? ["all"] : []),
                ...filteredForm,
              ].map((key) => {
                return (
                  <Controller
                    key={key}
                    name={key as any}
                    control={control}
                    render={({ field }) => {
                      return (
                        <div
                          className={`flex align-items-center ${
                            key === "all" ? "mb-2" : "ml-5"
                          }`}
                        >
                          <Checkbox
                            inputId={key}
                            checked={field.value}
                            disabled={false}
                            required={false}
                            handleOnChange={function (
                              e: CheckboxChangeParams
                            ): void {
                              if (key === "all") {
                                reset({
                                  ...ConfigsExportOptions.reduce(
                                    (result, el) => ({
                                      ...result,
                                      [el.value]: e.checked,
                                    }),
                                    {}
                                  ),
                                  all: e.checked,
                                });
                              } else {
                                setValue(
                                  key as any,
                                  e.checked,
                                  REVALIDATE_FORM_PROPS
                                );

                                const { all, ...rest } = getValues();
                                const currentForm = Object.fromEntries(
                                  Object.entries(rest).filter(([key, _]) =>
                                    filteredForm.includes(key)
                                  )
                                );

                                const allNewValue = Object.values(
                                  currentForm
                                ).every((el) => el === true);

                                if (allNewValue !== getValues("all"))
                                  setValue(
                                    "all",
                                    allNewValue,
                                    REVALIDATE_FORM_PROPS
                                  );
                              }
                            }}
                            inputRef={field.ref}
                            {...field}
                          />
                          <label htmlFor={key} className="ml-2">
                            {t(
                              key === "all"
                                ? "Applications.selectAllLabel"
                                : `Menu.${
                                    ConfigsExportOptions.find(
                                      (opt) => opt.value === key
                                    )?.label
                                  }`
                            )}
                          </label>
                        </div>
                      );
                    }}
                  />
                );
              })}
            </div>
          </>
        )}
      </>
    </Dialog>
  );
};
